import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gtmPageView, preloadPurchaseKillSwitch } from "@/lib/gtmDataLayer";

/**
 * Scroll-to-top + SPA virtual page_view.
 *
 * IMPORTANT: GA4 Config Tag in GTM (GTM-T8PVRK33) has `send_page_view: true`
 * by default, which fires a page_view automatically on the first load.
 * If we ALSO push a manual page_view on the first effect run, GA4 records
 * two page_views per session start → inflated session counts and an
 * incorrect page_title (Helmet hasn't updated `document.title` yet on
 * first commit, so the title would actually be the previous-state title).
 *
 * Fix: skip the first effect — only fire page_view on SUBSEQUENT route
 * changes (true SPA virtual pageviews). The initial page_view is owned
 * exclusively by GA4 Config Tag.
 *
 * NOTE: retryPendingPurchase removed — server-side webhook
 * (track-purchase-server-side) is the single source of truth for
 * purchase events. Browser hint events fire only on the order
 * confirmation page.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const killSwitchLoaded = useRef(false);
  const isFirstNavigation = useRef(true);

  // Preload kill-switch flag once per session
  useEffect(() => {
    if (!killSwitchLoaded.current) {
      killSwitchLoaded.current = true;
      preloadPurchaseKillSwitch();
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (isFirstNavigation.current) {
      // Skip — GA4 Config Tag already fires the initial page_view automatically.
      isFirstNavigation.current = false;
      return;
    }

    // Delay so Helmet has time to update document.title for the new route.
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        gtmPageView(pathname, document.title);
      }, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return null;
};

export default ScrollToTop;
